import { useGetFeatureFlagLaunchDarkly } from "@coherehealth/core-platform-api";
import { useEffect, useState } from "react";
import config from "../api/config";

export interface UseLaunchDarklyFeatureFlagProps {
  featureFlagName: string;
  context?: { [key: string]: unknown } | null;
}

interface CacheEntry {
  value: boolean;
  isLoading: boolean;
  promise?: Promise<unknown>;
}

const featureFlagCache = new Map<string, CacheEntry>();

function getCacheKey(flagName: string, context?: UseLaunchDarklyFeatureFlagProps["context"]): string {
  return `${flagName}:${JSON.stringify(context ?? {})}`;
}

export function useLaunchDarklyFeatureFlag(props: UseLaunchDarklyFeatureFlagProps) {
  const cacheKey = getCacheKey(props.featureFlagName, props.context);
  const [featureFlagEnabled, setFeatureFlagEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { mutate: fetchFeatureFlag } = useGetFeatureFlagLaunchDarkly({ base: config.SERVICE_API_URL });

  useEffect(() => {
    // Stale flag: set to true in the cleanup so that if cacheKey changes mid-flight (e.g. the
    // no-context fetch started before findACaseContext resolved), the old fetch cannot overwrite
    // the newer, correct result that arrived after the context became available.
    let stale = false;

    const cachedEntry = featureFlagCache.get(cacheKey);

    if (cachedEntry && !cachedEntry.isLoading) {
      setFeatureFlagEnabled(cachedEntry.value);
      setIsLoading(false);
      return;
    }

    if (cachedEntry?.promise) {
      cachedEntry.promise.then(() => {
        if (stale) {
          return;
        }
        const updatedEntry = featureFlagCache.get(cacheKey);
        if (updatedEntry) {
          setFeatureFlagEnabled(updatedEntry.value);
          setIsLoading(false);
        }
      });
      return;
    }

    const fetchPromise = fetchFlag();
    featureFlagCache.set(cacheKey, {
      value: false,
      isLoading: true,
      promise: fetchPromise,
    });

    async function fetchFlag() {
      try {
        const response = await fetchFeatureFlag({
          flagName: props.featureFlagName,
          context: props.context,
        });

        const value = response?.kind === "ERROR" ? false : response?.value ?? false;

        if (response?.kind === "ERROR") {
          console.error("Feature flag error:", response.errorKind);
        }

        featureFlagCache.set(cacheKey, { value, isLoading: false });
        if (!stale) {
          setFeatureFlagEnabled(value);
        }
      } catch (error) {
        console.error("Error fetching feature flag:", error);
        featureFlagCache.set(cacheKey, { value: false, isLoading: false });
        if (!stale) {
          setFeatureFlagEnabled(false);
        }
      } finally {
        if (!stale) {
          setIsLoading(false);
        }
      }
    }

    return () => {
      stale = true;
    };
  }, [cacheKey, fetchFeatureFlag, props.context, props.featureFlagName]);

  return { featureFlagEnabled, isLoading };
}
